import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useOnboardingStore } from "../../../stores/onboardingStore";
import {
  useEntitlementEntity,
  useEntitlement,
  useEntitlementsAny,
} from "../../../hooks/useEntitlements";
import { isDevFeaturesEnabled } from "../../../utils/environment";
import { useChatStore } from "../../../stores/chatStore";
import {
  PageInfoProvider,
  usePageInfo,
} from "../../../contexts/PageInfoContext";
import { TenantDropdown } from "../TenantDropdown";
import { AMCustomerDropdown } from "../AMCustomerDropdown";
import { ReportIssueButton } from "../../support-tickets/ReportIssueButton";
import NotificationPanel from "../../notifications/NotificationPanel";
import Rail, { RAIL_W_EXPANDED } from "./Rail";
import { useRailPinned } from "./useRailPreferences";
import SubNav from "./SubNav";
import Topbar from "./Topbar";
import CommandPalette from "./CommandPalette";
import { useRuleScopeCounts } from "./useRuleScopeCounts";
import {
  RAIL_NAV,
  AM_RAIL_NAV,
  getActiveRailId,
  filterNavItems,
  buildNavRouteMap,
  type EntitlementMap,
} from "./NavData";
import { addRecentItem } from "../../../utils/recentItems";

// Built once at module load — all static nav paths mapped to their display label.
const NAV_ROUTE_MAP = buildNavRouteMap();
import { SettingsIcon, LogoutIcon, XIcon, PinIcon } from "./ShellIcons";
import { useSavedCaseViews } from "../../../hooks/useSavedCaseViews";
import { useReadinessScore } from "../../../hooks/useReadinessScore";
import type { BaseComponentProps } from "../../../types";

// ─── Breakpoint hooks ─────────────────────────────────────────────────────────

function useIsMobile(breakpoint = 767): boolean {
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth <= breakpoint,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

function useIsTablet(maxWidth = 1023, minWidth = 768): boolean {
  const [isTablet, setIsTablet] = useState(
    () => window.innerWidth >= minWidth && window.innerWidth <= maxWidth,
  );
  useEffect(() => {
    const mq = window.matchMedia(
      `(min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`,
    );
    const handler = (e: MediaQueryListEvent) => setIsTablet(e.matches);
    mq.addEventListener("change", handler);
    setIsTablet(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, [maxWidth, minWidth]);
  return isTablet;
}

// ─── User avatar menu ─────────────────────────────────────────────────────────

interface UserMenuProps {
  user: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    email?: string;
  } | null;
  anchorEl: HTMLElement | null;
  /** Left offset — positioned just right of the rail (collapsed or pinned). */
  leftPx: number | string;
  onClose: () => void;
  onSettings: () => void;
  onLogout: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
  user,
  anchorEl,
  leftPx,
  onClose,
  onSettings,
  onLogout,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!anchorEl) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        e.target !== anchorEl
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [anchorEl, onClose]);

  if (!anchorEl) return null;

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="Account options"
      style={{
        position: "fixed",
        bottom: 56,
        left: leftPx,
        zIndex: 200,
        minWidth: 200,
        background: "var(--bg-elev)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-md)",
        boxShadow: "var(--sh-pop)",
        overflow: "hidden",
        padding: "6px",
      }}
    >
      {/* User info */}
      {user && (
        <div
          style={{
            padding: "8px 10px 10px",
            borderBottom: "1px solid var(--line)",
            marginBottom: 6,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)" }}>
            {user.displayName}
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
            {user.email}
          </div>
        </div>
      )}

      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onClose();
          onSettings();
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "7px 10px",
          borderRadius: "var(--r-xs)",
          border: "none",
          background: "transparent",
          color: "var(--ink-1)",
          fontSize: 13,
          cursor: "pointer",
          textAlign: "left",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            "var(--bg-sunken)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            "transparent";
        }}
      >
        <SettingsIcon size={14} />
        Settings
      </button>

      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onClose();
          onLogout();
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "7px 10px",
          borderRadius: "var(--r-xs)",
          border: "none",
          background: "transparent",
          color: "var(--ink-1)",
          fontSize: 13,
          cursor: "pointer",
          textAlign: "left",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            "var(--bg-sunken)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            "transparent";
        }}
      >
        <LogoutIcon size={14} />
        Logout
      </button>
    </div>
  );
};

// ─── Mobile nav overlay ───────────────────────────────────────────────────────

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const MobileNavOverlay: React.FC<MobileNavProps> = ({
  open,
  onClose,
  children,
}) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "var(--scrim)",
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        style={{
          position: "relative",
          width: "280px",
          height: "100%",
          background: "var(--bg)",
          borderRight: "1px solid var(--line)",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ink-1)",
            }}
          >
            Navigation
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            style={{
              width: 32,
              height: 32,
              borderRadius: "var(--r-sm)",
              border: "none",
              background: "transparent",
              color: "var(--ink-2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <XIcon size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// ─── Mobile flat nav list ─────────────────────────────────────────────────────

interface MobileNavListProps {
  items: ReturnType<typeof filterNavItems>;
  activePath: string;
  onNavigate: (path: string) => void;
}

const MobileNavList: React.FC<MobileNavListProps> = ({
  items,
  activePath,
  onNavigate,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div style={{ padding: "8px 0" }}>
      {items.map((item) => {
        const Icon = item.icon;
        const hasChildren = item.sub && item.sub.length > 0;
        const isExpanded = expanded === item.id;
        const isLeafActive = item.path === activePath;
        const isChildActive =
          hasChildren && item.sub!.some((s) => s.path === activePath);

        return (
          <React.Fragment key={item.id}>
            <button
              type="button"
              onClick={() => {
                if (hasChildren) {
                  setExpanded(isExpanded ? null : item.id);
                } else if (item.path) {
                  onNavigate(item.path);
                }
              }}
              aria-expanded={hasChildren ? isExpanded : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: "100%",
                padding: "9px 16px",
                border: "none",
                background:
                  isLeafActive || isChildActive
                    ? "var(--bg-sunken)"
                    : "transparent",
                color:
                  isLeafActive || isChildActive
                    ? "var(--ink-1)"
                    : "var(--ink-2)",
                fontSize: 14,
                fontWeight: isLeafActive || isChildActive ? 600 : 400,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <Icon size={16} />
              <span style={{ flex: 1 }}>{item.label}</span>
              {hasChildren && (
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: 11,
                    color: "var(--ink-3)",
                    transform: isExpanded ? "rotate(90deg)" : "none",
                    transition: "transform 150ms",
                  }}
                >
                  ›
                </span>
              )}
            </button>
            {hasChildren &&
              isExpanded &&
              item.sub!.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onNavigate(s.path)}
                  aria-current={activePath === s.path ? "page" : undefined}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "8px 16px 8px 44px",
                    border: "none",
                    background:
                      activePath === s.path ? "var(--bg-elev)" : "transparent",
                    color:
                      activePath === s.path ? "var(--ink-1)" : "var(--ink-2)",
                    fontSize: 13,
                    fontWeight: activePath === s.path ? 600 : 400,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {s.label}
                </button>
              ))}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─── AppShell ─────────────────────────────────────────────────────────────────

interface AppShellProps extends BaseComponentProps {
  children: React.ReactNode;
}

const AppShellInner: React.FC<AppShellProps> = ({
  children,
  className,
  "data-testid": dataTestId,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isAccountManager } = useAuth();
  const { windowState, sessions, toggleChat } = useChatStore();
  const { pageInfo } = usePageInfo();
  const { loadStatus, status: onboardingStatus } = useOnboardingStore();
  const isMobile = useIsMobile();

  // Only toggle the chat panel if there are active sessions or the panel is already open
  const handleChatToggle = useCallback(() => {
    if (sessions.length === 0 && !windowState.isOpen) return;
    toggleChat();
  }, [sessions.length, windowState.isOpen, toggleChat]);
  const isTablet = useIsTablet();

  // Notification panel anchor
  const [notifAnchorEl, setNotifAnchorEl] = useState<HTMLElement | null>(null);
  // User menu anchor
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  // Mobile nav overlay
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Tablet: subnav slide-in overlay
  const [tabletSubNavOpen, setTabletSubNavOpen] = useState(false);
  // Command palette (Cmd/Ctrl+K)
  const [commandOpen, setCommandOpen] = useState(false);

  // Global hotkey: Cmd/Ctrl+K opens the command palette.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Persisted rail "pinned open" preference (UX review option C).
  const [railPinned, setRailPinned] = useRailPinned();
  // Rail grid column width: wider when pinned so labels have room.
  const railCol = railPinned ? RAIL_W_EXPANDED : "var(--rail-w)";

  // Active rail section
  const [activeRailId, setActiveRailId] = useState(() =>
    getActiveRailId(location.pathname),
  );

  // Sync active section with location and record the visit in recent history.
  useEffect(() => {
    setActiveRailId(getActiveRailId(location.pathname));
    const label = NAV_ROUTE_MAP.get(location.pathname);
    if (label) {
      addRecentItem({ kind: "Nav", label, to: location.pathname });
    }
  }, [location.pathname]);

  // Load onboarding status
  useEffect(() => {
    if (user?.companyId && !onboardingStatus) {
      loadStatus(user.companyId);
    }
  }, [user?.companyId, onboardingStatus, loadStatus]);

  // ─── Entitlement checks (preserved from Sidebar.tsx) ─────────────────────
  const hasCaseAccess = useEntitlementEntity("Case");
  const hasDocumentAccess = useEntitlementEntity("Document");
  const hasUserAccess = useEntitlementEntity("User");
  const hasTenantAccess = useEntitlementEntity("Tenant");
  const hasPromptAccess = useEntitlementEntity("Prompt");
  const hasDocumentTypeAccess = useEntitlementEntity("DocumentType");
  const hasRoleAccess = useEntitlementEntity("Role");
  const hasRuleAccess = useEntitlementEntity("Rule");
  const hasRuleCategoryAccess = useEntitlementEntity("RuleCategory");
  const hasJobAccess = useEntitlementEntity("Job");
  const hasQuestionnaireAccess = useEntitlementEntity("Questionnaire");
  const hasAssessmentAccess = useEntitlementEntity("Assessment");
  const hasAssessmentTemplateAccess =
    useEntitlementEntity("AssessmentTemplate");
  const hasSubscriptionPackageAccess = useEntitlementEntity(
    "SubscriptionPackage",
  );
  const hasSubscriptionAccess = useEntitlementEntity("Subscription");
  const hasSubscriptionUpdateStatus = useEntitlement(
    "Subscription.UpdateStatus",
  );
  const hasAuditLogAccess = useEntitlement("Audit.Read");
  const hasCostExplorerAccess = useEntitlement("ApiCallCost.Read");
  const hasLabelTaggingAccess = useEntitlementEntity("LabelTagging");
  const hasSupportTicketReadAccess = useEntitlementsAny([
    "SupportTicket.ReadOwn",
    "SupportTicket.ReadTenant",
  ]);
  const hasSupportTriageAccess = useEntitlementsAny([
    "SupportTicket.ReadAll",
    "SupportTicket.ReadAssigned",
  ]);
  const hasSupportAnalyticsAccess = useEntitlementsAny([
    "SupportAnalytics.Tenant",
    "SupportAnalytics.All",
  ]);

  // Live readiness score for the topbar chip — only fetches when the user
  // has both Case and Rule access; otherwise returns undefined and the
  // chip is hidden.
  const readinessScore = useReadinessScore({
    enabled: hasCaseAccess && hasRuleAccess,
  });

  const hasUserManagementAccess =
    hasUserAccess || hasTenantAccess || hasRoleAccess;
  const hasAdminAccess = hasPromptAccess || hasDocumentTypeAccess;
  const hasComplianceAccess =
    hasDocumentAccess ||
    hasRuleAccess ||
    hasRuleCategoryAccess ||
    hasJobAccess ||
    hasQuestionnaireAccess;
  const hasSubscriptionsMenuAccess =
    hasSubscriptionPackageAccess || hasSubscriptionAccess;

  // Top-level visibility (maps rail item IDs to booleans)
  const topLevelVisibility: EntitlementMap = {
    cases: hasCaseAccess,
    healthcare: hasComplianceAccess,
    documents: hasDocumentAccess,
    "questionnaire-management": hasQuestionnaireAccess,
    "needs-assessment": hasAssessmentAccess || hasAssessmentTemplateAccess,
    subscriptions: hasSubscriptionsMenuAccess,
    "user-management": hasUserManagementAccess,
    admin: hasAdminAccess,
    "osv-export": hasDocumentAccess || hasRuleAccess,
    "account-managers": !!isAdmin,
    "audit-logs": !!isAdmin && hasAuditLogAccess,
    "cost-explorer": hasCostExplorerAccess,
    "label-tagging": hasLabelTaggingAccess,
    "support-tickets":
      hasSupportTicketReadAccess ||
      hasSupportTriageAccess ||
      hasSupportAnalyticsAccess,
    debug: isDevFeaturesEnabled(),
  };

  // Child-level visibility
  const childVisibility: Record<string, boolean> = {
    tenants: hasTenantAccess,
    users: hasUserAccess,
    roles: hasRoleAccess,
    "admin-document-types": hasDocumentTypeAccess,
    "admin-prompts": hasPromptAccess,
    "healthcare-upload": hasDocumentAccess,
    "chapter-upload": hasDocumentAccess,
    "healthcare-files": hasDocumentAccess,
    "healthcare-processing": hasJobAccess,
    "healthcare-rules": hasRuleAccess || hasRuleCategoryAccess,
    "healthcare-rule-assignments": hasRuleAccess,
    "admin-rule-assignments": hasRuleAccess,
    "compliance-questionnaire":
      hasQuestionnaireAccess && !!user?.tenantOnboardingCompleted,
    "questionnaires-list": !!isAdmin && hasQuestionnaireAccess,
    "questionnaires-create": !!isAdmin && hasQuestionnaireAccess,
    "subscription-plans": hasSubscriptionPackageAccess,
    "subscription-create": hasSubscriptionPackageAccess && !!isAdmin,
    "subscription-manage": hasSubscriptionAccess,
    "subscription-review": hasSubscriptionUpdateStatus,
    "support-tickets-list": hasSupportTicketReadAccess,
    "support-tickets-triage": hasSupportTriageAccess,
    "support-tickets-analytics": hasSupportAnalyticsAccess,
  };

  const visibleItems = isAccountManager
    ? AM_RAIL_NAV
    : filterNavItems(
        RAIL_NAV,
        topLevelVisibility,
        !!isAdmin,
        isDevFeaturesEnabled(),
        childVisibility,
        !!user?.tenantOnboardingCompleted,
      );

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleRailPick = useCallback(
    (id: string) => {
      setActiveRailId(id);
      const item = visibleItems.find((n) => n.id === id);
      if (!item) return;
      if (item.path) {
        navigate(item.path);
      } else if (item.sub && item.sub.length > 0) {
        // Navigate to first child
        navigate(item.sub[0].path);
      }
    },
    [navigate, visibleItems],
  );

  const handleSubNavNavigate = useCallback(
    (path: string) => {
      navigate(path);
      if (isMobile) setMobileNavOpen(false);
      if (isTablet) setTabletSubNavOpen(false);
    },
    [navigate, isMobile, isTablet],
  );

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  // ─── Computed values ──────────────────────────────────────────────────────

  const baseActiveSection = visibleItems.find((n) => n.id === activeRailId);

  // Inject user-saved views into the Cases section's pinned list at runtime.
  const { views: savedViews, removeView } = useSavedCaseViews();
  // Live counts for the Rules sub-nav (only fetched while that section is open).
  const ruleScopeCounts = useRuleScopeCounts(
    baseActiveSection?.id === "healthcare",
  );
  const activeSection = React.useMemo(() => {
    if (!baseActiveSection) return baseActiveSection;
    if (baseActiveSection.id === "cases") {
      const pinned = savedViews.map((v) => ({
        id: v.id,
        label: v.label,
        path: `/cases/open${v.search ? `?${v.search}` : ""}`,
        icon: PinIcon,
      }));
      return { ...baseActiveSection, pinned };
    }
    if (baseActiveSection.id === "healthcare" && ruleScopeCounts) {
      const sub = baseActiveSection.sub?.map((s) => {
        if (s.id === "healthcare-rules-all")
          return { ...s, count: ruleScopeCounts.all };
        if (s.id === "healthcare-rules-hcm")
          return { ...s, count: ruleScopeCounts.hcm };
        if (s.id === "healthcare-rules-svp")
          return { ...s, count: ruleScopeCounts.svp };
        if (s.id === "healthcare-rules-340b")
          return { ...s, count: ruleScopeCounts.b340 };
        return s;
      });
      return { ...baseActiveSection, sub };
    }
    return baseActiveSection;
  }, [baseActiveSection, savedViews, ruleScopeCounts]);

  const hasSubNav = !!(activeSection?.sub && activeSection.sub.length > 0);
  const userInitials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase() ||
      "?"
    : "?";

  // Chat window width adjustment (desktop only)
  const chatWidth =
    !isMobile && windowState.isOpen && !windowState.isMinimized
      ? windowState.width
      : 0;

  // Breadcrumbs from PageInfoContext
  const breadcrumbs =
    pageInfo.breadcrumbs.length > 0
      ? pageInfo.breadcrumbs.map((b) => ({ label: b.label, path: b.href }))
      : undefined;

  // ─── Grid layout ─────────────────────────────────────────────────────────
  // Areas: "rail topbar topbar" / "rail subnav main" (when hasSubNav)
  //        "rail topbar"        / "rail main"         (when !hasSubNav)

  // Tablet layout: rail + full-width main; subnav is a slide-in overlay
  const tabletGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: `${railCol} 1fr`,
    gridTemplateRows: "var(--topbar-h) 1fr",
    gridTemplateAreas: `"rail topbar" "rail main"`,
    height: "100vh",
    overflow: "hidden",
    marginRight: chatWidth > 0 ? `${chatWidth}px` : undefined,
  };

  const gridStyle: React.CSSProperties = isMobile
    ? {
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "var(--topbar-h) 1fr",
        gridTemplateAreas: `"topbar" "main"`,
        height: "100vh",
        overflow: "hidden",
        marginRight: chatWidth > 0 ? `${chatWidth}px` : undefined,
      }
    : isTablet
      ? tabletGridStyle
      : hasSubNav
        ? {
            display: "grid",
            gridTemplateColumns: `${railCol} var(--subnav-w) 1fr`,
            gridTemplateRows: "var(--topbar-h) 1fr",
            gridTemplateAreas: `"rail topbar topbar" "rail subnav main"`,
            height: "100vh",
            overflow: "hidden",
            marginRight: chatWidth > 0 ? `${chatWidth}px` : undefined,
          }
        : {
            display: "grid",
            gridTemplateColumns: `${railCol} 1fr`,
            gridTemplateRows: "var(--topbar-h) 1fr",
            gridTemplateAreas: `"rail topbar" "rail main"`,
            height: "100vh",
            overflow: "hidden",
            marginRight: chatWidth > 0 ? `${chatWidth}px` : undefined,
          };

  return (
    <>
      <div className={className} data-testid={dataTestId} style={gridStyle}>
        {/* Rail — desktop only */}
        {!isMobile && (
          <Rail
            items={visibleItems}
            activeId={activeRailId}
            onPick={handleRailPick}
            pinned={railPinned}
            onTogglePin={() => setRailPinned((p) => !p)}
            userInitials={userInitials}
            onUserClick={() => {
              // Find the avatar button in Rail to anchor the menu
              const btn = document.querySelector<HTMLElement>(
                '[aria-label="Account menu"]',
              );
              setUserMenuAnchorEl(btn);
            }}
          />
        )}

        {/* Topbar */}
        <Topbar
          breadcrumbs={breadcrumbs}
          hasNotification={true}
          onNotificationClick={(e) => setNotifAnchorEl(e.currentTarget)}
          onSearchClick={() => setCommandOpen(true)}
          onMenuClick={() => setMobileNavOpen(true)}
          isMobile={isMobile}
          isTablet={isTablet}
          subNavOpen={tabletSubNavOpen}
          onSubNavToggle={
            isTablet && hasSubNav
              ? () => setTabletSubNavOpen((v) => !v)
              : undefined
          }
          readiness={readinessScore}
          tenantSlot={
            !isMobile ? (
              isAccountManager ? (
                <AMCustomerDropdown topbar />
              ) : isAdmin ? (
                <TenantDropdown isAdmin={isAdmin} topbar />
              ) : undefined
            ) : undefined
          }
        >
          {/* TenantDropdown for admin users — in topbar on mobile */}
          {isMobile && isAccountManager && <AMCustomerDropdown />}
          {isMobile && isAdmin && !isAccountManager && (
            <TenantDropdown isAdmin={isAdmin} />
          )}

          {/* Global "Report Issue" — opens the ticket dialog from any screen,
              prefilled with a screenshot + subject derived from the active
              sidebar section (matches whatever the rail highlights as current). */}
          <ReportIssueButton
            compact={isMobile}
            pageLabel={activeSection?.label}
          />

          {/* ── Global AI Chat toggle ──
               WCAG 2.1 AA:
                 - aria-label describes state + session count (2.4.6, 4.1.2)
                 - aria-expanded communicates open/closed state (4.1.2)
                 - aria-haspopup="dialog" signals a panel will appear (4.1.2)
                 - 36×36 touch target matches notification bell (Fitts's Law)
               UX laws:
                 - Von Restorff: ✦ sparkle icon + accent colour on active state
                 - Zeigarnik Effect: session badge keeps unresolved chats visible
                 - Fitts's Law: same 36×36 target as adjacent notification bell */}
          <button
            type="button"
            aria-label={
              sessions.length > 0
                ? `AI Chat — ${sessions.length} active session${sessions.length !== 1 ? "s" : ""}`
                : "AI Chat"
            }
            aria-expanded={windowState.isOpen}
            aria-haspopup="dialog"
            onClick={handleChatToggle}
            disabled={sessions.length === 0 && !windowState.isOpen}
            title={
              sessions.length === 0 && !windowState.isOpen
                ? "Open a case to start chatting"
                : undefined
            }
            style={{
              position: "relative",
              width: 36,
              height: 36,
              borderRadius: "var(--r-sm)",
              border: "none",
              background: windowState.isOpen
                ? "color-mix(in srgb, var(--accent) 12%, var(--bg-elev))"
                : "transparent",
              color:
                sessions.length === 0 && !windowState.isOpen
                  ? "var(--ink-4)"
                  : windowState.isOpen
                    ? "var(--accent)"
                    : "var(--ink-2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor:
                sessions.length === 0 && !windowState.isOpen
                  ? "not-allowed"
                  : "pointer",
              transition: "background 100ms, color 100ms",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (!windowState.isOpen) {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "var(--bg-elev)";
                (e.currentTarget as HTMLButtonElement).style.color =
                  "var(--ink-1)";
              }
            }}
            onMouseLeave={(e) => {
              if (!windowState.isOpen) {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "transparent";
                (e.currentTarget as HTMLButtonElement).style.color =
                  "var(--ink-2)";
              }
            }}
          >
            {/* ✦ sparkle — distinct from generic icons (Von Restorff) */}
            <span
              aria-hidden="true"
              style={{ fontSize: 16, lineHeight: 1, fontStyle: "normal" }}
            >
              ✦
            </span>

            {/* Session count badge (Zeigarnik Effect) */}
            {sessions.length > 0 && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  minWidth: 14,
                  height: 14,
                  borderRadius: 7,
                  background: "var(--accent)",
                  color: "var(--accent-ink)",
                  fontSize: 9,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  padding: "0 3px",
                  border: "1.5px solid var(--bg)",
                }}
              >
                {sessions.length > 9 ? "9+" : sessions.length}
              </span>
            )}
          </button>
        </Topbar>

        {/* SubNav — desktop permanent column */}
        {!isMobile && !isTablet && hasSubNav && (
          <SubNav
            section={activeSection}
            activePath={location.pathname + location.search}
            onNavigate={handleSubNavNavigate}
            onRemovePinned={
              activeSection?.id === "cases" ? removeView : undefined
            }
          />
        )}

        {/* Main content */}
        <main
          role="main"
          aria-label="Main content area"
          style={{
            gridArea: "main",
            overflowY: "auto",
            overflowX: "clip",
            background: "var(--bg)",
            padding: "var(--gap)",
            minHeight: 0,
          }}
        >
          {children}
        </main>
      </div>

      {/* Tablet subnav slide-in overlay (outside the grid so it layers above) */}
      {isTablet && hasSubNav && (
        <>
          {/* Scrim — tap to close */}
          {tabletSubNavOpen && (
            <div
              aria-hidden="true"
              onClick={() => setTabletSubNavOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 24,
                background: "var(--scrim)",
              }}
            />
          )}
          {/* Slide panel */}
          <div
            role="navigation"
            aria-label="Section navigation"
            aria-hidden={!tabletSubNavOpen}
            style={{
              position: "fixed",
              top: "var(--topbar-h)",
              left: railCol,
              bottom: 0,
              width: "var(--subnav-w)",
              background: "var(--bg)",
              borderRight: "1px solid var(--line)",
              zIndex: 25,
              overflowY: "auto",
              boxShadow: tabletSubNavOpen ? "var(--sh-2)" : "none",
              transform: tabletSubNavOpen
                ? "translateX(0)"
                : "translateX(-100%)",
              transition: "transform 200ms ease, box-shadow 200ms ease",
              pointerEvents: tabletSubNavOpen ? "auto" : "none",
            }}
          >
            <SubNav
              section={activeSection}
              activePath={location.pathname + location.search}
              onNavigate={handleSubNavNavigate}
              onRemovePinned={
                activeSection?.id === "cases" ? removeView : undefined
              }
            />
          </div>
        </>
      )}

      {/* Notification panel (portal) */}
      <NotificationPanel
        anchorEl={notifAnchorEl}
        open={Boolean(notifAnchorEl)}
        onClose={() => setNotifAnchorEl(null)}
      />

      {/* User menu (portal) */}
      <UserMenu
        user={user}
        anchorEl={userMenuAnchorEl}
        leftPx={
          railPinned
            ? `calc(${RAIL_W_EXPANDED} + 8px)`
            : "calc(var(--rail-w) + 8px)"
        }
        onClose={() => setUserMenuAnchorEl(null)}
        onSettings={() => navigate("/account/settings")}
        onLogout={handleLogout}
      />

      {/* Mobile nav overlay */}
      <MobileNavOverlay
        open={isMobile && mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      >
        {/* TenantDropdown for admins in mobile nav */}
        {isAccountManager && <AMCustomerDropdown />}
        {isAdmin && !isAccountManager && <TenantDropdown isAdmin={isAdmin} />}

        <MobileNavList
          items={visibleItems}
          activePath={location.pathname + location.search}
          onNavigate={handleSubNavNavigate}
        />
      </MobileNavOverlay>

      {/* Command palette (Cmd/Ctrl+K) */}
      <CommandPalette
        open={commandOpen}
        onClose={() => setCommandOpen(false)}
      />
    </>
  );
};

const AppShell: React.FC<AppShellProps> = (props) => {
  return (
    <PageInfoProvider>
      <AppShellInner {...props} />
    </PageInfoProvider>
  );
};

export default AppShell;
